import { BaSeDialogContext } from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import {
  CERTIFICADOS_ENDPOINT,
  CERTIFICADO_ENDPOINT,
  ENDPOINT_CERTIFICADO,
  USER_ENDPOINT,
} from './constants';

export function useGetExemploByIdService(id) {
  const [result, setResult] = React.useState();
  const [loading, setLoading] = React.useState();
  const [error, setError] = React.useState();

  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    if (!id) {
      return;
    }

    setResult(null);
    setError(false);
    setLoading(true);

    backendGet(`${ENDPOINT_CERTIFICADO}/${id}/gerado`)
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return [result, loading, error];
}

export const useGetUserCertificationsByCpf = (cpf, ordem, busca = null) => {
  const [result, setResult] = React.useState();
  const [loading, setLoading] = React.useState();
  const [error, setError] = React.useState();

  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    if (!cpf) {
      return;
    }

    setResult(null);
    setError(false);
    setLoading(true);

    backendGet(
      `${USER_ENDPOINT}/certificados?ordem=${ordem}${
        !!busca ? `&busca=${busca}` : ''
      }`,
    )
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpf, ordem, busca]);
  return [result, loading, error];
};

export const useCertificadoSituacaoService = (id) => {
  const [result, setResult] = React.useState();
  const [loading, setLoading] = React.useState();
  const [error, setError] = React.useState();

  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    if (!id) {
      return;
    }

    setResult(null);
    setError(false);
    setLoading(true);

    backendGet(`${CERTIFICADO_ENDPOINT}/${id}/situacao`)
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [backendGet, id]);
  return [result, loading, error];
};

export const useCertificadoService = (id) => {
  const [result, setResult] = React.useState();
  const [loading, setLoading] = React.useState();
  const [error, setError] = React.useState();

  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    if (!id) {
      return;
    }

    setResult(null);
    setError(false);
    setLoading(true);

    backendGet(`${CERTIFICADO_ENDPOINT}/${id}`)
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [backendGet, id]);
  return [result, loading, error];
};

export const useCertificadosService = () => {
  const { backendPost, backendPut } = Sebrae.useBackend();
  const { showNotification } = React.useContext(BaSeDialogContext);
  const messageSuccess =
    'Reprocessamento para geração do certificado solicitado com sucesso!';
  const messageError =
    'Não foi possível realizar o envio para o reprocessamento!';

  const sendReprocessing = React.useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (ids, onSuccess, onAchieve, cpf, filter) => {
      backendPost(
        `${CERTIFICADOS_ENDPOINT}${
          !!cpf ? `?cpf=${cpf}${!!filter ? `&filter=${filter}` : ''}` : ''
        }`,
        ids,
      )
        .then(
          () =>
            showNotification({
              message: messageSuccess,
              color: 'confirmation',
            }),
          onSuccess({ resetSelection: true }),
        )
        .catch((error) =>
          showNotification({
            message: messageError,
            color: 'destructive',
          }),
        )
        .finally(() => onAchieve());
    },
    [backendPost, showNotification],
  );

  const sendReprocessingAllError = React.useCallback(
    (cpf, onSuccess, onAchieve) =>
      backendPost(`${CERTIFICADOS_ENDPOINT}/erro${!!cpf ? `?cpf=${cpf}` : ''}`)
        .then(
          () =>
            showNotification({
              message: messageSuccess,
              color: 'confirmation',
            }),
          onSuccess({ resetSelection: true }),
        )
        .catch((error) =>
          showNotification({
            message: messageError,
            color: 'destructive',
          }),
        )
        .finally(() => onAchieve()),
    [backendPost, showNotification],
  );

  const deleteSelectedItems = React.useCallback(
    (paramsDelete, onSuccess, onAchieve) => {
      const query = `?filter=${paramsDelete.filter}&tipoBusca=${paramsDelete.tipoBusca}&listaModelos=${paramsDelete.listaModelos}`;
      backendPost(
        `${CERTIFICADOS_ENDPOINT}/lote/exclusao${query}`,
        paramsDelete.loteDTO,
      )
        .then(
          () =>
            showNotification({
              message: 'Registro excluído com sucesso!',
              color: 'confirmation',
            }),
          onSuccess({ resetSelection: true }),
        )
        .catch((error) =>
          showNotification({
            message: 'Não foi possível realizar a exclusão!',
            color: 'destructive',
          }),
        )
        .finally(() => onAchieve());
    },
    [backendPost, showNotification],
  );

  const changeValuesCertificadoLote = React.useCallback(
    (params, onSuccess, onAchieve) => {
      backendPut(`${CERTIFICADO_ENDPOINT}/lote`, params)
        .then(
          () =>
            showNotification({
              message: 'Certificados alterados com sucesso!',
              color: 'confirmation',
            }),
          onSuccess({ resetSelection: true }),
        )
        .catch((error) =>
          showNotification({
            message: 'Não foi possível alterar certificados!',
            color: 'destructive',
          }),
        )
        .finally(() => onAchieve());
    },
    [backendPut, showNotification],
  );

  const handleSendEmailCertificadosLote = React.useCallback(
    (params, onSuccess, onAchieve) => {
      const query = `?filter=${params.filter}&tipoBusca=${params.tipoBusca}&listaModelos=${params.listaModelos}`;
      backendPost(`${CERTIFICADO_ENDPOINT}/lote/email${query}`, params.loteDTO)
        .then(
          () =>
            showNotification({
              message: 'Reenvio de e-mails ao cliente solicitado com sucesso!',
              color: 'confirmation',
            }),
          //onSuccess({ resetSelection: true }),
        )
        .catch((error) =>
          showNotification({
            message:
              'Não foi possível realizar o envio dos certificados por e-mail!',
            color: 'destructive',
          }),
        )
        .finally(() => onAchieve);
    },
    [backendPost, showNotification],
  );

  return {
    sendReprocessing,
    sendReprocessingAllError,
    deleteSelectedItems,
    changeValuesCertificadoLote,
    handleSendEmailCertificadosLote,
  };
};

export const useTemplateCertificadoSasService = () => {
  const [listTemplates, setListTemplates] = React.useState();
  const [loadingTemplate, setLoadingTemplate] = React.useState();
  const [errorTemplate, setErrorTemplate] = React.useState();

  const { showNotification } = React.useContext(BaSeDialogContext);
  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    setListTemplates([]);
    setErrorTemplate(false);
    setLoadingTemplate(true);

    backendGet(`${CERTIFICADO_ENDPOINT}/selecao`)
      .then((data) => {
        const templates = data?.map((item) => ({
          id: item.id,
          label: item.descricao,
        }));
        setListTemplates(templates);
      })
      .catch((error) => {
        setErrorTemplate(true);
        showNotification({
          message: 'Não foi possível recuperar lista de templates',
          supportMessage: error.message,
          color: 'destructive',
        });
      })
      .finally(() => setLoadingTemplate(false));
  }, [backendGet, showNotification]);
  return { listTemplates, loadingTemplate, errorTemplate };
};
